import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Truck, Package, MessageSquare, Clock, CheckCircle, AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { PageHeader, SectionCard, StatusBadge } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { API_BASE } from '../api';

type OrderStatus = 'new' | 'processing' | 'shipped' | 'delivered' | 'exception' | 'cancelled';

const CARRIERS = [
  { value: 'USPS', label: 'USPS' },
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHL Express', label: 'DHL Express' },
  { value: 'DHL eCommerce', label: 'DHL eCommerce' },
  { value: 'China Post', label: 'China Post (中国邮政)' },
  { value: 'SF Express', label: 'SF Express (顺丰)' },
  { value: 'YTO Express', label: 'YTO Express (圆通)' },
  { value: 'Yanwen', label: 'Yanwen (燕文)' },
  { value: '4PX', label: '4PX (递四方)' },
  { value: 'Canada Post', label: 'Canada Post' },
  { value: 'Royal Mail', label: 'Royal Mail' },
  { value: 'Australia Post', label: 'Australia Post' },
  { value: 'Other', label: 'Other' },
];

interface OrderDetail {
  id: number;
  shopify_order_id: string | null;
  status: OrderStatus;
  fulfillment_deadline: string | null;
  internal_notes: string | null;
  total_amount: string | null;
  line_items: unknown;
  created_at: string;
  updated_at: string;
}

interface OrderItem {
  id: number;
  order_item_fact_id: number | null;
  quantity_fulfilled: number;
  shipment_id: number | null;
}

interface Shipment {
  id: number;
  tracking_number: string | null;
  tracking_url: string | null;
  carrier: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  status: string;
  shopify_fulfillment_id: string | null;
  created_at: string;
}

const statusVariantMap: Record<OrderStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  new: 'info', processing: 'warning', shipped: 'success', delivered: 'success', exception: 'error', cancelled: 'neutral',
};

const statusIcon: Record<string, React.ReactNode> = {
  new: <Clock className="w-3.5 h-3.5" />,
  processing: <Package className="w-3.5 h-3.5" />,
  shipped: <Truck className="w-3.5 h-3.5" />,
  delivered: <CheckCircle className="w-3.5 h-3.5" />,
  exception: <AlertTriangle className="w-3.5 h-3.5" />,
};

export default function VendorOrderDetail() {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToast();
  const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  const fmtDate = (s: string) => new Date(s).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [_items, setItems] = useState<OrderItem[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);

  const [showShipForm, setShowShipForm] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('USPS');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [shipping, setShipping] = useState(false);

  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => { loadOrder(); }, [id]);

  const loadOrder = async () => {
    try {
      const res = await fetch(`${API_BASE}/vendor/orders/${id}`, { credentials: 'include' });
      const json = await res.json();
      if (res.ok) {
        setOrder(json.data.order || json.data);
        setItems(json.data.items || []);
        setShipments(json.data.shipments || []);
        setNoteText(json.data.order?.internal_notes || json.data?.internal_notes || '');
      } else {
        addToast(json.error || t('common:error'), 'error');
      }
    } catch { addToast(t('common:error'), 'error'); }
    finally { setLoading(false); }
  };

  const handleShip = async () => {
    if (!trackingNumber) { addToast(t('orders:detail.trackingRequired'), 'error'); return; }
    setShipping(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/orders/${id}/ship`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tracking_number: trackingNumber,
          carrier,
          tracking_url: trackingUrl || undefined,
        }),
      });
      const json = await res.json();
      if (res.ok) {
        const sync = json.data?.shopify_sync;
        if (sync?.success) {
          addToast(t('orders:shopifySyncSuccess'), 'success');
        } else if (!order?.shopify_order_id) {
          addToast(t('orders:shopifyNotLinked'), 'info');
        } else if (sync?.error) {
          addToast(t('orders:shopifySyncFail', { error: sync.error }), 'warning');
        } else {
          addToast(t('common:success'), 'success');
        }
        setShowShipForm(false);
        setTrackingNumber('');
        setCarrier('USPS');
        setTrackingUrl('');
        loadOrder();
      } else {
        addToast(json.error || t('common:error'), 'error');
      }
    } catch { addToast(t('common:error'), 'error'); }
    finally { setShipping(false); }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/orders/${id}/notes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: noteText }),
      });
      if (res.ok) addToast(t('common:success'), 'success');
    } catch { addToast(t('common:error'), 'error'); }
    finally { setSavingNote(false); }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const res = await fetch(`${API_BASE}/vendor/orders/${id}/status`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) { addToast(t('common:success'), 'success'); loadOrder(); }
    } catch { addToast(t('common:error'), 'error'); }
  };

  const backBtn = { label: t('common:back'), onClick: () => navigate('/vendor/orders') };

  if (loading) {
    return (
      <div className="space-y-6 max-w-5xl">
        <PageHeader title={`${t('orders:detail.title')} #${id}`} back={backBtn} />
        <div className="card p-8"><div className="h-6 w-48 bg-white/5 animate-pulse rounded" /></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6 max-w-5xl">
        <PageHeader title={t('orders:detail.title')} back={backBtn} />
        <div className="card p-8 text-center text-white/40">{t('orders:detail.notFound')}</div>
      </div>
    );
  }

  const canShip = ['new', 'processing'].includes(order.status);
  const lineItems = Array.isArray(order.line_items) ? order.line_items : [];

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title={`${t('orders:detail.title')} #${order.shopify_order_id || order.id}`}
        subtitle={t('orders:detail.createdAt', { date: fmtDate(order.created_at) })}
        back={backBtn}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge variant={statusVariantMap[order.status]}>
              {t(`orders:status.${order.status}`)}
            </StatusBadge>
            {order.fulfillment_deadline && (
              <span className="text-xs text-white/30">
                {t('orders:detail.dueDate', { date: fmtDate(order.fulfillment_deadline) })}
              </span>
            )}
          </div>
        }
      />

      <div className="flex gap-2 flex-wrap">
        {canShip && (
          <button onClick={() => setShowShipForm(true)} className="btn-primary flex items-center gap-2">
            <Truck className="w-4 h-4" />{t('orders:shipOrder')}
          </button>
        )}
        {order.status === 'new' && (
          <button onClick={() => handleStatusChange('processing')} className="btn-secondary flex items-center gap-2">
            <Package className="w-4 h-4" />{t('orders:detail.startProcessing')}
          </button>
        )}
      </div>

      {showShipForm && (
        <SectionCard title={t('orders:enterTracking')}>
          <div className="p-5 space-y-4 max-w-lg">
            {order.shopify_order_id && (
              <div className="flex items-center gap-2 px-3 py-2 rounded text-xs" style={{ backgroundColor: 'rgba(255,212,0,0.08)', border: '1px solid rgba(255,212,0,0.15)' }}>
                <ExternalLink className="w-3.5 h-3.5 text-[#FFD400] shrink-0" />
                <span className="text-white/60">
                  Shopify #{order.shopify_order_id} — {t('orders:shipOrder')}
                </span>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('orders:carrier')}</label>
              <select
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className="input w-full"
              >
                {CARRIERS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('orders:trackingNumber')} *</label>
              <input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="input w-full"
                placeholder="e.g. 9400111899223100..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('orders:trackingUrl')}</label>
              <input
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                className="input w-full"
                placeholder="https://..."
              />
              <p className="text-[10px] text-white/30">{t('orders:trackingUrlHint')}</p>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={handleShip} disabled={shipping} className="btn-primary flex items-center gap-2">
                {shipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                {shipping ? t('common:loading') : t('orders:markShipped')}
              </button>
              <button onClick={() => setShowShipForm(false)} className="btn-ghost">{t('common:cancel')}</button>
            </div>
          </div>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SectionCard title={t('orders:detail.lineItems')}>
            {lineItems.length === 0 ? (
              <div className="p-5 text-sm text-white/30">{t('orders:detail.noLineItems')}</div>
            ) : (
              <div className="p-5 space-y-3">
                {lineItems.map((item: Record<string, unknown>, idx: number) => (
                  <div key={idx} className="flex items-center gap-3 py-3" style={{ borderBottom: idx < lineItems.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                    <div className="w-10 h-10 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                      <Package className="w-4 h-4 text-white/30" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/80 truncate">{(item.title as string) || t('orders:detail.item')}</p>
                      {item.sku ? <p className="text-xs text-white/30">SKU: {String(item.sku)}</p> : null}
                    </div>
                    <span className="text-sm text-white/50">x{(item.quantity as number) || 1}</span>
                    <span className="text-sm font-medium text-white">
                      ${(((item.price as number) || 0) * ((item.quantity as number) || 1)).toFixed(2)}
                    </span>
                  </div>
                ))}
                {order.total_amount && (
                  <div className="flex justify-end pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="text-sm text-white/40 mr-3">{t('common:total')}</span>
                    <span className="text-lg font-bold text-white">${parseFloat(order.total_amount).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        </div>

        <div>
          <SectionCard title={t('orders:detail.shipmentHistory')}>
            {shipments.length === 0 ? (
              <div className="p-5 text-sm text-white/30">{t('orders:detail.noShipments')}</div>
            ) : (
              <div className="p-5 space-y-4">
                {shipments.map((s) => (
                  <div key={s.id} className="relative pl-6" style={{ borderLeft: '2px solid rgba(255,255,255,0.08)' }}>
                    <div className="absolute -left-1.5 top-0 w-3 h-3 rounded-full" style={{ backgroundColor: s.delivered_at ? '#4ade80' : '#FFD400' }} />
                    <div className="space-y-1">
                      <p className="text-sm text-white/80">{s.carrier || t('orders:carrier')}</p>
                      {s.tracking_number && (
                        s.tracking_url ? (
                          <a href={s.tracking_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#FFD400] font-mono hover:underline">
                            {s.tracking_number}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <p className="text-xs text-[#FFD400] font-mono">{s.tracking_number}</p>
                        )
                      )}
                      <p className="text-xs text-white/30">
                        {s.shipped_at ? t('orders:detail.shippedAt', { date: fmtDate(s.shipped_at) }) : t('orders:detail.pendingShipment')}
                        {s.delivered_at && ` · ${t('orders:detail.deliveredAt', { date: fmtDate(s.delivered_at) })}`}
                      </p>
                      {s.shopify_fulfillment_id && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-white/25">
                          <CheckCircle className="w-2.5 h-2.5" /> Shopify synced
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      <SectionCard title={t('orders:internalNotes')}>
        <div className="p-5 space-y-3">
          <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
            className="input w-full min-h-[80px]" placeholder={t('orders:detail.notesPlaceholder')} />
          <button onClick={handleSaveNote} disabled={savingNote} className="btn-ghost flex items-center gap-2 text-sm">
            <MessageSquare className="w-3.5 h-3.5" />
            {savingNote ? t('common:loading') : t('orders:addNote')}
          </button>
        </div>
      </SectionCard>

      <SectionCard title={t('orders:detail.timeline')}>
        <div className="p-5">
          <div className="flex items-center gap-2">
            {(['new', 'processing', 'shipped', 'delivered'] as const).map((s, idx) => {
              const isActive = ['new', 'processing', 'shipped', 'delivered'].indexOf(order.status) >= idx;
              const isCurrent = order.status === s;
              return (
                <div key={s} className="flex items-center gap-2">
                  {idx > 0 && <div className="w-8 h-px" style={{ backgroundColor: isActive ? '#FFD400' : 'rgba(255,255,255,0.08)' }} />}
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: isActive ? '#FFD400' : 'rgba(255,255,255,0.04)',
                        color: isActive ? '#0B0B0B' : 'rgba(255,255,255,0.3)',
                        border: isCurrent ? '2px solid #FFD400' : 'none',
                      }}>
                      {statusIcon[s]}
                    </div>
                    <span className={`text-[10px] ${isActive ? 'text-white/60' : 'text-white/20'}`}>
                      {t(`orders:status.${s}`)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
